import * as React from "react"
import { useArticle } from "../../../hooks/news/useArticle"
import { useParams } from "react-router-dom"
import H1 from "../../atoms/typography/H1"
import HeroSection from "../../contentful/HeroSection"
import RichText from "../../molecules/RichText"
import ArticleMetadata from "../../molecules/news/ArticleMetadata"
import PageContentWrapper from "../../organisms/PageContentWrapper"
import Button from "../../atoms/buttons/Button"
import { ArrowBack } from "../../atoms/icons/ArrowBack"
import SignUpForNewsForm from "../../organisms/SharePageForm"
import Metadata from "../../contentful/Metadata"
import { HorizontalSpacer } from "../../atoms/spacers"
import RelatedLinks from "../../molecules/news/RelatedLinks"
import { Col, Row } from "react-grid-system"
import FormSection from "../../contentful/FormSection"
import ShareLinks from "../../organisms/ShareLinks"
import { useContentfulInspectorMode, useContentfulLiveUpdates } from "@contentful/live-preview/react"
import { ContentfulLiveUpdates } from "../../../graphql/contentful/client"
import LoadingSpinner from "../../atoms/LoadingSpinner"

interface ArticlePageProps {
  slug: string
}

const ArticlePage: React.FC<ArticlePageProps> = ({ slug }) => {
  const { article: unliveArticle, loading, error, query } = useArticle(slug)
  const article = useContentfulLiveUpdates(unliveArticle, { query })
  const inspectorProps = useContentfulInspectorMode({ entryId: article?.sys?.id })

  if (error) {
    return <h1>{error?.message}</h1>
  }

  if (loading || !article) {
    return (
      <div>
        <HeroSection />
        <PageContentWrapper>
          <HorizontalSpacer spacing={2} />
          <Row>
            <Col xs="content">
              <Button title="Back to resources" variant="light" size="small" iconLeft={<ArrowBack />} link="/news" />
            </Col>
          </Row>
          <HorizontalSpacer spacing={2} />
          <LoadingSpinner />
          <HorizontalSpacer spacing={2} />
        </PageContentWrapper>
      </div>
    )
  }

  const { header, metadata, content, tags, sys, title, description } = article

  return (
    <div style={{ backgroundColor: "white" }}>
      <Metadata __typename="Metadata" description={description} tags={tags} title={title} type="article" />
      {header && <HeroSection showTitle={false} __typename="HeroSection" sys={header.sys} image={header} />}
      <PageContentWrapper>
        <HorizontalSpacer spacing={2} />
        <Row>
          <Col xs="content">
            <Button title="Back to resources" variant="light" size="small" iconLeft={<ArrowBack />} link="/news" />
          </Col>
        </Row>
        <HorizontalSpacer spacing={2} />
        {title && (
          <H1 bold {...inspectorProps({ fieldId: "title" })}>
            {title}
          </H1>
        )}
        <HorizontalSpacer spacing={2} />
        {tags && sys && <ArticleMetadata tags={tags} publishedAt={new Date(sys.publishedAt)} />}
        <HorizontalSpacer spacing={2} />
      </PageContentWrapper>
      {content && <RichText rtf={content} />}
      <PageContentWrapper>
        <HorizontalSpacer spacing={1} />
        {metadata?.relatedLinks && <RelatedLinks links={metadata?.relatedLinks} />}
        <HorizontalSpacer spacing={3} />
      </PageContentWrapper>
      <FormSection>
        <SignUpForNewsForm />
        <HorizontalSpacer spacing={3} />
        <ShareLinks />
      </FormSection>
    </div>
  )
}

export const ArticlePageWithSlug = () => {
  const { slug } = useParams()

  if (!slug) {
    return <H1>No slug</H1>
  }

  return (
    <ContentfulLiveUpdates>
      <ArticlePage slug={slug} />
    </ContentfulLiveUpdates>
  )
}
